import type { ProductOption } from '@shared/components/ui/ProductCombobox';
import { DynamicComboboxOption } from '@shared/components/ui/DynamicCombobox';
import { useCallback, useMemo } from 'react';
import type { ProductFilters } from '../models/Product';
import { useProducts } from './useProducts';

export interface UseProductSearchOptions {
  /** Filtro team_id (cliente hijo) para listados 3PL */
  tenantChildId?: number;
  /** Si es false, no se ejecuta la query (p. ej. 3PL sin cliente seleccionado) */
  enabled?: boolean;
}

export const useProductSearch = (options?: UseProductSearchOptions) => {
  const enabled = options?.enabled ?? true;
  const productFilters: ProductFilters = useMemo(
    () => ({
      limit: 10000,
      fields: 'basic',
      ...(options?.tenantChildId != null && options.tenantChildId > 0 ? { tenantChildId: options.tenantChildId } : {}),
    }),
    [options?.tenantChildId],
  );

  // 🎯 Usar el hook existente para obtener todos los productos
  const { products, isLoading, error } = useProducts(productFilters, { enabled });

  // 🎯 Memoizar las opciones de productos (formato simple para DynamicCombobox)
  const productOptions = useMemo(() => {
    // Verificar que products sea un array antes de hacer map
    if (!products || !Array.isArray(products)) {
      return [];
    }
    const options = products.map((product) => ({
      label: product.name || `Producto ${product.id}`,
      value: product.id.toString(),
    }));
    return options;
  }, [products]);

  // 🎯 Memoizar las opciones de productos (formato completo para ProductCombobox)
  const productOptionsForCombobox = useMemo(() => {
    // Verificar que products sea un array antes de hacer map
    if (!products || !Array.isArray(products)) {
      return [];
    }
    const options = products.map((product) => ({
      id: product.id,
      name: product.name || `Producto ${product.id}`,
      code: product.code,
      image: product.image_hash || product.image,
      value: product.id.toString(),
    }));
    return options;
  }, [products]);

  // 🎯 Función de búsqueda LOCAL (sin llamadas a API)
  const searchProducts = useCallback(
    async (query: string): Promise<DynamicComboboxOption[]> => {
      // Simular comportamiento async para mantener compatibilidad
      return new Promise((resolve) => {
        // Usar setTimeout para no bloquear el UI, aunque sea instantáneo
        setTimeout(() => {
          if (!query.trim()) {
            // Sin query, devolver los primeros 100 productos
            resolve(productOptions.slice(0, 100));
            return;
          }

          // Filtrar localmente por nombre del producto
          const filtered = productOptions.filter((product) =>
            product.label.toLowerCase().includes(query.toLowerCase()),
          );

          // Limitar a 200 resultados para performance óptima
          resolve(filtered.slice(0, 200));
        }, 0);
      });
    },
    [productOptions],
  );

  // 🎯 Función de búsqueda para ProductCombobox (con todos los datos del producto)
  const searchProductsForCombobox = useCallback(
    async (query: string): Promise<ProductOption[]> => {
      // Simular comportamiento async para mantener compatibilidad
      return new Promise((resolve) => {
        setTimeout(() => {
          if (!query.trim()) {
            // Sin query, devolver los primeros 100 productos
            resolve(productOptionsForCombobox.slice(0, 100));
            return;
          }

          // Filtrar localmente por nombre O código del producto
          const filtered = productOptionsForCombobox.filter((product) => {
            const queryLower = query.toLowerCase();
            const matchesName = product.name.toLowerCase().includes(queryLower);
            const matchesCode = product.code?.toLowerCase().includes(queryLower);
            return matchesName || matchesCode;
          });

          // Limitar a 200 resultados para performance óptima
          resolve(filtered.slice(0, 200));
        }, 0);
      });
    },
    [productOptionsForCombobox],
  );

  return {
    searchProducts, // Para DynamicCombobox (formato simple)
    searchProductsForCombobox, // 🆕 Para ProductCombobox (formato completo)
    products,
    isLoading, // Para mostrar loading inicial
    error, // Para mostrar errores de carga inicial
    totalProducts: products?.length || 0, // Info útil
    allProductsWithStock: productOptionsForCombobox, // 🆕 Todos los productos con stock para enriquecer
  };
};
